import {
  createGaiaToken,
  deleteGaiaToken,
  exportVaultFileToGaia as exportVaultFileToGaiaApi,
  getGaiaToken,
  getSectionFilesMeta,
  getSectionGroups,
  importGaiaFile,
  updateGaiaToken,
  type GaiaFileMeta,
  type GaiaSectionGroupSlim,
  type GaiaTokenCreate,
  type GaiaTokenInDb,
  type GaiaTokenUpdate,
  type SectionFilesQuery,
  type VaultFileInDb,
} from '@/api';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { logger } from '@/utils/logger';

const FILES_PER_PAGE = 5;

export interface SectionWithFiles {
  section: GaiaSectionGroupSlim['sections'][0];
  files: GaiaFileMeta[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalFiles: number;
  hasMore: boolean;
}

interface AsyncState<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

interface PagedResponse<T> {
  data: T[] | null;
  page: number | null;
  pages: number | null;
  total: number | null;
}

export const useGaiaStore = defineStore('gaia', () => {
  const sectionGroups = ref<AsyncState<GaiaSectionGroupSlim[]>>({
    data: [],
    loading: false,
    error: null,
  });
  const sectionFiles = ref<Record<string, SectionWithFiles>>({});
  const currentToken = ref<GaiaTokenInDb | null>(null);
  const tokenLoading = ref<boolean>(false);

  function setSectionFiles(id: string, data: SectionWithFiles): void {
    sectionFiles.value = { ...sectionFiles.value, [id]: data };
  }

  function updateSectionFiles(id: string, updates: Partial<SectionWithFiles>): void {
    if (!(id in sectionFiles.value)) return;
    sectionFiles.value = {
      ...sectionFiles.value,
      [id]: { ...sectionFiles.value[id], ...updates },
    };
  }

  function clearSectionFiles(): void {
    sectionFiles.value = {};
  }

  function clearAll(): void {
    sectionGroups.value = { data: [], loading: false, error: null };
    sectionFiles.value = {};
    currentToken.value = null;
  }

  async function withToken<T>(fn: () => Promise<T>): Promise<T | null> {
    tokenLoading.value = true;
    try {
      if (!currentToken.value) await fetchCurrentToken();
      if (!currentToken.value) return null;
      return await fn();
    } catch (error) {
      logger.error('Failed to run action with GAIA token', error);
      throw error;
    } finally {
      tokenLoading.value = false;
    }
  }

  async function loadPage(
    sectionId: string,
    page: number
  ): Promise<PagedResponse<GaiaFileMeta> | null> {
    const query: SectionFilesQuery = {
      page,
      per_page: FILES_PER_PAGE,
    };
    const response = await getSectionFilesMeta({
      path: { section_id: sectionId },
      body: query,
    });

    if (!response.data) {
      throw new Error('Failed to load section files');
    }
    return response.data;
  }

  function updateSectionState(
    sectionId: string,
    response: PagedResponse<GaiaFileMeta>,
    append: boolean = false
  ): void {
    if (!(sectionId in sectionFiles.value)) return;
    const currentSection = sectionFiles.value[sectionId];

    if (response.data) {
      const updatedFiles = append ? [...currentSection.files, ...response.data] : response.data;

      updateSectionFiles(sectionId, {
        files: updatedFiles,
        loading: false,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        currentPage: response.page || 1,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        totalPages: response.pages || 1,
        totalFiles: response.total ?? 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        hasMore: (response.page || 1) < (response.pages || 1),
      });
    }
  }

  function setSectionError(sectionId: string, errorMessage: string): void {
    updateSectionFiles(sectionId, {
      error: errorMessage,
      loading: false,
    });
  }

  // Actions
  async function fetchCurrentToken(): Promise<GaiaTokenInDb | null> {
    tokenLoading.value = true;
    try {
      const response = await getGaiaToken();
      if (response.response?.status !== 200) {
        throw new Error('Could not fetch current token');
      }

      if (response.data?.token) {
        currentToken.value = response.data;
      } else {
        currentToken.value = null;
      }
      return currentToken.value;
    } catch (error) {
      logger.error('Error fetching GAIA token:', error);
      currentToken.value = null;
      return null;
    } finally {
      tokenLoading.value = false;
    }
  }

  async function createToken(tokenData: GaiaTokenCreate): Promise<GaiaTokenInDb> {
    tokenLoading.value = true;
    try {
      const response = await createGaiaToken({ body: tokenData });

      if (!response.data) {
        throw new Error('Failed to create GAIA token');
      }
      const newToken = response.data;
      currentToken.value = newToken;
      return newToken;
    } catch (error) {
      logger.error('Error creating GAIA token:', error);
      throw error;
    } finally {
      tokenLoading.value = false;
    }
  }

  async function updateToken(tokenData: GaiaTokenUpdate): Promise<GaiaTokenInDb> {
    const result = await withToken(async () => {
      const response = await updateGaiaToken({ body: tokenData });

      if (!response.data) {
        throw new Error('Failed to update GAIA token');
      }
      const updatedToken = response.data;
      currentToken.value = updatedToken;
      return updatedToken;
    });

    if (!result) {
      throw new Error('No existing GAIA token found to update');
    }

    return result;
  }

  async function deleteToken(): Promise<void> {
    const result = await withToken(async () => {
      await deleteGaiaToken();

      currentToken.value = null;
      sectionGroups.value = { data: [], loading: false, error: null };
      clearSectionFiles();
    });

    if (result === null) {
      throw new Error('No existing GAIA token found to delete');
    }
  }

  async function fetchSectionGroups(scope: 'read' | 'write' = 'read'): Promise<void> {
    await withToken(async () => {
      sectionGroups.value.loading = true;
      sectionGroups.value.error = null;
      const response = await getSectionGroups({ query: { scope } });

      if (!response.data) {
        throw new Error('Failed to fetch GAIA section groups');
      }
      const groups = response.data;
      sectionGroups.value = { data: groups, loading: false, error: null };
    }).catch(() => {
      sectionGroups.value = {
        data: [],
        loading: false,
        error: `Failed to load GAIA section groups with ${scope} scope`,
      };
    });
  }

  async function fetchSectionFiles(sectionId: string): Promise<void> {
    if (sectionId in sectionFiles.value) return;

    const section = findSectionById(sectionId);
    if (!section) {
      logger.error('Section not found:', sectionId);
      return;
    }

    setSectionFiles(sectionId, {
      section,
      files: [],
      loading: true,
      error: null,
      currentPage: 1,
      totalPages: 1,
      totalFiles: 0,
      hasMore: false,
    });

    await withToken(async () => {
      const response = await loadPage(sectionId, 1);
      if (response) {
        updateSectionState(sectionId, response, false);
      }
    }).catch(() => {
      setSectionError(sectionId, 'Failed to load section files');
    });
  }

  async function loadMoreFiles(sectionId: string): Promise<void> {
    if (!(sectionId in sectionFiles.value)) return;
    const currentSection = sectionFiles.value[sectionId];
    if (!currentSection.hasMore) return;

    updateSectionFiles(sectionId, { loading: true });

    await withToken(async () => {
      const nextPage = currentSection.currentPage + 1;
      const response = await loadPage(sectionId, nextPage);
      if (response) {
        updateSectionState(sectionId, response, true);
      }
    })
      .catch(() => {
        setSectionError(sectionId, 'Failed to load more files');
      })
      .finally(() => {
        updateSectionFiles(sectionId, { loading: false });
      });
  }

  function findSectionById(sectionId: string): GaiaSectionGroupSlim['sections'][0] | null {
    for (const group of sectionGroups.value.data) {
      for (const section of group.sections) {
        if (section.id === sectionId) {
          return section;
        }
      }
    }
    return null;
  }

  function getGaiaFileById(fileId: string): GaiaFileMeta | null {
    for (const sectionData of Object.values(sectionFiles.value)) {
      const file = sectionData.files.find(f => f.id === fileId);
      if (file) {
        return file;
      }
    }
    return null;
  }

  async function importGaiaFileToVault(
    fileId: string,
    parentDirectoryUid?: string
  ): Promise<VaultFileInDb> {
    try {
      const response = await importGaiaFile({
        path: { file_id: fileId },
        query: parentDirectoryUid ? { parent_directory_uid: parentDirectoryUid } : undefined,
      });
      if (!response.data) {
        throw new Error('Failed to import GAIA file to vault');
      }
      const importedFile = response.data;
      return importedFile;
    } catch (error) {
      logger.error('Error importing GAIA file to vault:', error);
      throw error;
    }
  }

  async function exportVaultFileToGaia(vaultFileUid: string, gaiaSectionId: string): Promise<void> {
    try {
      await exportVaultFileToGaiaApi({
        path: {
          gaia_section_id: gaiaSectionId,
        },
        query: {
          vault_file_uid: vaultFileUid,
        },
      });
    } catch (error) {
      logger.error('Error exporting vault file to GAIA:', error);
      throw error;
    }
  }

  function clearError(): void {
    sectionGroups.value.error = null;
  }

  return {
    // State
    sectionGroups,
    sectionFiles,
    currentToken,
    tokenLoading,

    // Actions
    fetchCurrentToken,
    createToken,
    updateToken,
    deleteToken,
    fetchSectionGroups,
    fetchSectionFiles,
    loadMoreFiles,
    getGaiaFileById,
    importGaiaFileToVault,
    exportVaultFileToGaia,
    clearError,
    clearSectionFiles,
    clearAll,
  };
});
